/**
 * Device Info Utilities
 * Browser and device detection for audit records
 */

/**
 * Get device type based on user agent
 * @returns {string} - Device type (Desktop, Mobile, Tablet)
 */
const getDeviceType = () => {
  const ua = navigator.userAgent;

  if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) {
    return 'Tablet';
  }

  if (/Mobile|iP(hone|od)|Android|BlackBerry|IEMobile|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(ua)) {
    return 'Mobile';
  }

  return 'Desktop';
};

/**
 * Get browser name and version
 * @returns {string} - Browser info (e.g., "Chrome 120")
 */
const getBrowserInfo = () => {
  const ua = navigator.userAgent;
  let browser = 'Unknown';
  let version = '';

  if (ua.includes('Firefox/')) {
    browser = 'Firefox';
    version = ua.match(/Firefox\/(\d+)/)?.[1] || '';
  } else if (ua.includes('Edg/')) {
    browser = 'Edge';
    version = ua.match(/Edg\/(\d+)/)?.[1] || '';
  } else if (ua.includes('Chrome/')) {
    browser = 'Chrome';
    version = ua.match(/Chrome\/(\d+)/)?.[1] || '';
  } else if (ua.includes('Safari/') && !ua.includes('Chrome')) {
    browser = 'Safari';
    version = ua.match(/Version\/(\d+)/)?.[1] || '';
  } else if (ua.includes('Opera') || ua.includes('OPR/')) {
    browser = 'Opera';
    version = ua.match(/(?:Opera|OPR)\/(\d+)/)?.[1] || '';
  }

  return version ? `${browser} ${version}` : browser;
};

/**
 * Get operating system info
 * @returns {string} - OS info (e.g., "Windows 11", "macOS")
 */
const getOSInfo = () => {
  const ua = navigator.userAgent;

  if (ua.includes('Windows NT 10.0')) {
    return ua.includes('Windows NT 10.0; Win64') ? 'Windows 11' : 'Windows 10';
  }
  if (ua.includes('Windows NT 6.3')) return 'Windows 8.1';
  if (ua.includes('Windows NT 6.2')) return 'Windows 8';
  if (ua.includes('Windows NT 6.1')) return 'Windows 7';
  if (ua.includes('Mac OS X')) return 'macOS';
  if (ua.includes('Linux')) return 'Linux';
  if (ua.includes('Android')) return 'Android';
  if (ua.includes('iPhone') || ua.includes('iPad')) return 'iOS';

  return 'Unknown OS';
};

/**
 * Get full device info string
 * @returns {string} - Full device info (e.g., "Desktop - Windows 11")
 */
export const getDeviceString = () => {
  return `${getDeviceType()} - ${getOSInfo()}`;
};

/**
 * Get full browser string
 * @returns {string} - Full browser info
 */
export const getBrowserString = () => {
  return getBrowserInfo();
};

/**
 * Get mock location info (for demo purposes)
 * In production, this could use a geolocation API
 * @returns {Object} - Location info
 */
export const getLocationInfo = () => {
  // Mock location data for Mexico
  const locations = [
    { city: 'Ciudad de México', state: 'CDMX' },
    { city: 'Guadalajara', state: 'Jalisco' },
    { city: 'Monterrey', state: 'Nuevo León' },
    { city: 'Puebla', state: 'Puebla' },
    { city: 'Querétaro', state: 'Querétaro' }
  ];

  // Use the first one as default, or could randomize for demo
  const location = locations[0];

  return {
    city: location.city,
    state: location.state,
    country: 'México',
    ip: `192.168.${Math.floor(Math.random() * 255)}.${Math.floor(Math.random() * 255)}`
  };
};

/**
 * Get complete device info object for query records
 * @returns {Object} - Complete device info
 */
export const getDeviceInfo = () => ({
  device: getDeviceString(),
  browser: getBrowserString(),
  location: getLocationInfo()
});

export default {
  getDeviceType,
  getBrowserInfo,
  getOSInfo,
  getDeviceString,
  getBrowserString,
  getLocationInfo,
  getDeviceInfo
};
